/**
 * logoUrlCheck — what to tell a Super Admin about a practice-logo URL, from the URL itself plus
 * what the browser learned when it tried to render it (<img> load/error + natural size).
 *
 * WHY: a practice's LogoUrl was the doctor's 800x800 headshot for three weeks and two others
 * pointed at a homepage (HTML → broken image everywhere), all set at onboarding where nobody saw
 * the image. A live preview with plain-English verdicts is the fix at the entry point; the cap on
 * every render surface (logo-size-cap-guardrail.test.js) and the server-side image/* probe
 * (teamio-admin SET_SITE_LOGO) are the other two layers.
 *
 * Pure and import-free ON PURPOSE — cypress/node-tests/logo-url-check.test.js loads and executes
 * this exact file (same pattern as phiMask.js). Keep it that way.
 */

export const LOGO_CHECK = Object.freeze({
    EMPTY: 'empty',      // no URL — allowed, the practice name is shown instead
    INVALID: 'invalid',  // not a direct http(s) link — blocks
    PENDING: 'pending',  // browser hasn't reported load/error yet
    ERROR: 'error',      // browser could not render it as an image — blocks
    WARN: 'warn',        // renders, but looks suspicious (square, huge, photo-like)
    OK: 'ok',
});

export function isHttpUrl(u) {
    return /^https?:\/\/\S+$/i.test(String(u == null ? '' : u).trim());
}

/** A URL whose path ends in "/" or a page extension is almost certainly a web page, not an image. */
export function looksLikeWebPage(u) {
    const s = String(u == null ? '' : u).trim();
    try {
        const url = new URL(s);
        const p = url.pathname || '/';
        if (p === '/' || p.endsWith('/')) return true;
        const last = p.split('/').pop();
        return /\.(html?|php|aspx?|jsp)$/i.test(last);
    } catch (e) {
        return false;
    }
}

/** File names that usually mean a photo was pasted where a logo belongs. */
export function looksLikePhoto(u) {
    return /(photo|headshot|portrait|team|staff|doctor|\bdr[-_.]|selfie|hero|banner|profile)/i.test(String(u == null ? '' : u));
}

/**
 * @param {{url:string, loaded?:boolean, naturalWidth?:number, naturalHeight?:number}} input
 *   loaded: true after <img @load>, false after <img @error>, undefined while pending.
 * @returns {{level:string, messages:string[], width:number, height:number}}
 */
export function assessLogoUrl({ url, loaded, naturalWidth, naturalHeight } = {}) {
    const u = String(url == null ? '' : url).trim();
    const width = Number(naturalWidth) || 0;
    const height = Number(naturalHeight) || 0;

    if (!u) return { level: LOGO_CHECK.EMPTY, messages: ['No logo — the practice name will be shown instead.'], width: 0, height: 0 };
    if (!isHttpUrl(u)) {
        return { level: LOGO_CHECK.INVALID, messages: ['Must be a direct https:// link to an image file (PNG, JPG, SVG or WebP).'], width: 0, height: 0 };
    }
    if (loaded === false) {
        const why = looksLikeWebPage(u)
            ? 'This looks like a web page address, not an image file.'
            : 'The browser could not load this address as an image.';
        return {
            level: LOGO_CHECK.ERROR,
            messages: [`Could not load this as an image. ${why} On the practice website, right-click the logo → "Copy image address".`],
            width: 0, height: 0,
        };
    }
    if (loaded !== true) {
        const messages = ['Checking the image…'];
        if (looksLikeWebPage(u)) messages.push('This looks like a web page address, not an image file.');
        return { level: LOGO_CHECK.PENDING, messages, width: 0, height: 0 };
    }

    const messages = [];
    let level = LOGO_CHECK.OK;
    if (width > 0 && height > 0) {
        const ratio = width / height;
        if (ratio > 0.8 && ratio < 1.25) {
            level = LOGO_CHECK.WARN;
            messages.push(`Square image (${width}×${height}) — logos are usually wide. Is this a photo? A doctor's headshot was once saved as a logo.`);
        }
        if (width > 1000 || height > 1000) {
            level = LOGO_CHECK.WARN;
            messages.push(`Large image (${width}×${height}) — it will be scaled down everywhere, but a file under 1000px loads faster in emails.`);
        }
        if (height < 40) {
            level = LOGO_CHECK.WARN;
            messages.push(`Very small image (${width}×${height}) — it may look blurry.`);
        }
    }
    if (looksLikePhoto(u)) {
        level = LOGO_CHECK.WARN;
        messages.push('The file name suggests a photo, not a logo.');
    }
    if (level === LOGO_CHECK.OK) messages.push(width && height ? `Looks good — ${width}×${height}.` : 'Looks good.');
    return { level, messages, width, height };
}

/** Definite failures block saving/creating; warnings and "pending" do not. */
export function logoBlocksSave(assessment) {
    return !!assessment && (assessment.level === LOGO_CHECK.INVALID || assessment.level === LOGO_CHECK.ERROR);
}
